import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  getMyDemands,
  selectWorkflow,
} from "../../../state/ducks/workflow/index";
import RenderDemandDetail from "./RenderDemandDetail";
import StaticModal from "../shared/StaticModal";
import EminevimLoading from "../shared/EminevimLoading";

const ALL_REASON_FILTER = "all";
const EMPTY_REASON_LABEL = "Diğer";

const getDemandValue = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== "") {
      return value;
    }
  }
  return "";
};

const normalizeDemandText = (value) =>
  String(value || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");

const getDemandReasonLabel = (item) =>
  getDemandValue(item, "basvuruNedeni", "BasvuruNedeni", "islem", "Islem").trim() || EMPTY_REASON_LABEL;

const getDemandReasonKey = (item) =>
  normalizeDemandText(getDemandReasonLabel(item)) || ALL_REASON_FILTER;

const parseDemandDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDemandDateParts = (value) => {
  const date = parseDemandDate(value);
  if (!date) {
    return { date: "-", time: "" };
  }

  return {
    date: date.toLocaleDateString("tr-TR"),
    time: date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
  };
};

const normalizeHexColor = (colorCode) => {
  if (!colorCode || typeof colorCode !== "string") {
    return null;
  }

  let normalized = colorCode.trim();
  if (!normalized.startsWith("#")) {
    normalized = `#${normalized}`;
  }

  if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
    normalized = `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
  }

  return /^#[0-9a-fA-F]{6}$/.test(normalized) ? normalized : null;
};

const hexToRgba = (hex, alpha) => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    return null;
  }

  const value = normalized.replace("#", "");
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const getStatusClass = (status) => {
  const text = normalizeDemandText(status);
  if (text.includes("tamamland") || text.includes("sonucland") || text.includes("sonuçland")) {
    return "t-salesDocStatus--success";
  }
  if (text.includes("iptal")) {
    return "t-salesDocStatus--error";
  }
  if (text.includes("devam") || text.includes("atandi") || text.includes("atandı") || text.includes("isleme") || text.includes("işleme") || text.includes("incelen")) {
    return "t-salesDocStatus--review";
  }
  if (text.includes("acik") || text.includes("açık")) {
    return "t-salesDocStatus--waiting";
  }
  return "t-salesDocStatus--default";
};

class MyDemands extends Component {
  state = {
    reasonFilter: ALL_REASON_FILTER,
    isLoading: true,
    shouldAnimateCard: false,
  };

  componentDidMount() {
    this.props.getMyDemands();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.workflow?.list !== this.props.workflow?.list) {
      const nextState = {};

      if (this.state.isLoading) {
        nextState.isLoading = false;
        nextState.shouldAnimateCard = true;
        window.setTimeout(() => this.setState({ shouldAnimateCard: false }), 900);
      }

      const availableKeys = this.getReasonFilters()
        .map((filter) => filter.key)
        .filter((key) => key !== ALL_REASON_FILTER);

      if (this.state.reasonFilter !== ALL_REASON_FILTER && !availableKeys.includes(this.state.reasonFilter)) {
        nextState.reasonFilter = ALL_REASON_FILTER;
      }

      if (Object.keys(nextState).length > 0) {
        this.setState(nextState);
      }
    }
  }

  getReasonFilters = () => {
    const demands = this.props.workflow?.list || [];
    const groups = new Map();

    demands.forEach((item) => {
      const key = getDemandReasonKey(item);
      const label = getDemandReasonLabel(item);
      const current = groups.get(key);

      if (current) {
        current.count += 1;
      } else {
        groups.set(key, { key, label, count: 1 });
      }
    });

    const groupedFilters = Array.from(groups.values()).sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }

      return left.label.localeCompare(right.label, "tr");
    });

    return [{ key: ALL_REASON_FILTER, label: "Tümü", count: demands.length }, ...groupedFilters];
  };

  getDemandsForDisplay = () => {
    const demands = this.props.workflow?.list;
    if (!demands) return [];

    const { reasonFilter } = this.state;
    if (reasonFilter === ALL_REASON_FILTER) {
      return [...demands];
    }

    return demands.filter((item) => getDemandReasonKey(item) === reasonFilter);
  };

  getSortedDemands = () => {
    const demands = [...this.getDemandsForDisplay()];

    demands.sort((leftItem, rightItem) => {
      const leftTime = parseDemandDate(leftItem?.acilisTarihi)?.getTime() || 0;
      const rightTime = parseDemandDate(rightItem?.acilisTarihi)?.getTime() || 0;
      return rightTime - leftTime;
    });

    return demands;
  };

  handleReasonFilter = (key) => {
    this.setState({ reasonFilter: key });
  };

  renderDemandsLoading = () => (
    <EminevimLoading subtext="Talepleriniz hazırlanıyor, lütfen bekleyiniz." />
  );

  renderReasonFilters = () => {
    const filters = this.getReasonFilters();
    if (filters.length <= 1) {
      return null;
    }

    return (
      <div className="t-myDemands-filters" role="tablist" aria-label="Başvuru nedeni filtresi">
        {filters.map((filter) => (
          <button
            key={filter.key}
            type="button"
            role="tab"
            aria-selected={this.state.reasonFilter === filter.key}
            className={`t-myDemands-filterChip${
              this.state.reasonFilter === filter.key ? " is-active" : ""
            }`}
            onClick={() => this.handleReasonFilter(filter.key)}
          >
            {filter.label}
          </button>
        ))}
      </div>
    );
  };

  renderStatusBadge = (item) => {
    const status = getDemandValue(item, "durumu", "Durumu") || "-";
    const customStyle = (() => {
      const color = normalizeHexColor(getDemandValue(item, "statusColor", "StatusColor"));
      if (!color) {
        return null;
      }

      return {
        color,
        backgroundColor: hexToRgba(color, 0.12),
        borderColor: hexToRgba(color, 0.24),
      };
    })();

    return (
      <span
        className={`t-salesDocStatus t-myDemands-status${
          customStyle ? " t-salesDocStatus--custom" : ` ${getStatusClass(status)}`
        }`}
        style={customStyle || undefined}
      >
        {status}
      </span>
    );
  };

  renderTalepNoCell = (item) => {
    const iconClass = getDemandValue(item, "webIcon", "WebIcon") || "t-icon-file-text";
    const iconColor = normalizeHexColor(getDemandValue(item, "iconColor", "IconColor")) || "#0c9908";
    const secondaryId = getDemandValue(
      item,
      "referansNo",
      "ReferansNo",
      "uyusmazlikNo",
      "UyusmazlikNo"
    );

    return (
      <div className="t-myDemands-talepNo">
        <span
          className="t-myDemands-talepIcon"
          style={{ color: iconColor, backgroundColor: hexToRgba(iconColor, 0.14) }}
        >
          <i className={iconClass} aria-hidden="true" />
        </span>
        <span className="t-myDemands-talepNoText">
          <strong>{item.talepNo || "-"}</strong>
          {secondaryId ? <small>{secondaryId}</small> : null}
        </span>
      </div>
    );
  };

  renderDateCell = (value) => {
    const parts = formatDemandDateParts(value);
    return (
      <span className="t-myDemands-dateCell">
        <strong>{parts.date}</strong>
        {parts.time ? <small>{parts.time}</small> : null}
      </span>
    );
  };

  renderMobileCards = (demands) => (
    <div className="t-myDemands-mobileList">
      {demands.map((item) => (
        <article className="t-myDemands-mobileCard" key={item.talepNo}>
          <div className="t-myDemands-mobileCardHeader">
            <div className="t-myDemands-mobileCardIdentity">
              {this.renderTalepNoCell(item)}
            </div>
            <div className="t-myDemands-mobileCardHeaderRight">
              <RenderDemandDetail data={item} />
              {this.renderStatusBadge(item)}
            </div>
            <span className="t-myDemands-mobileCardDate">{this.renderDateCell(item.acilisTarihi)}</span>
          </div>

          <div className="t-myDemands-mobileGrid">
            <div className="t-myDemands-mobileField">
              <span className="t-myDemands-mobileLabel">Konu</span>
              <span className="t-myDemands-mobileValue">{item.konu || "-"}</span>
            </div>
            <div className="t-myDemands-mobileField">
              <span className="t-myDemands-mobileLabel">Başvuru Nedeni</span>
              <span className="t-myDemands-mobileValue">
                {getDemandValue(item, "basvuruNedeni", "BasvuruNedeni", "islem", "Islem") || "-"}
              </span>
            </div>
          </div>
        </article>
      ))}
    </div>
  );

  render() {
    const { shouldAnimateCard, isLoading, reasonFilter } = this.state;
    const sortedDemands = this.getSortedDemands();
    const totalDemands = (this.props.workflow?.list || []).length;

    return (
      <div className="t-myDemands-page" style={{ background: "#ffffff" }}>
        <div className="row" style={{ padding: "3%" }}>
          <div className="col-12">
            <div className="t-memberInfo-pageHeader t-myDemands-pageHeader">
              <div className="t-myDemands-pageHeaderText">
                <h1 className="t-memberInfo-pageTitle">Talep Takibi</h1>
                <p className="t-memberInfo-pageSubtitle">
                  Oluşturduğunuz talepleri görüntüleyebilir ve durumlarını takip edebilirsiniz.
                </p>
              </div>
              <div className="t-myDemands-pageHeaderActions">
                {!isLoading ? (
                  <span className="t-memberDetail-headerBadge">Toplam: {totalDemands}</span>
                ) : null}
                <Link
                  to="/Home/SubmitDemand"
                  className="t-memberInfo-btn t-memberInfo-btn--primary"
                >
                  <span>Yeni Talep</span>
                  <i className="t-icon-arrow-right2" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {isLoading ? (
              this.renderDemandsLoading()
            ) : (
              <article
                className={`t-memberInfo-card t-memberDetail-header--green${
                  shouldAnimateCard ? " t-memberInfo-card--enter" : ""
                }`}
              >
                {this.renderReasonFilters()}

                {sortedDemands.length > 0 ? (
                  this.renderMobileCards(sortedDemands)
                ) : (
                  <div className="t-memberInfo-emptyFilter">
                    {reasonFilter !== ALL_REASON_FILTER && totalDemands > 0
                      ? "Seçilen başvuru nedeninde talep bulunamadı."
                      : "Görüntülenecek talep bulunmuyor."}
                  </div>
                )}
              </article>
            )}
          </div>
        </div>

        <StaticModal />
        <div className="row" style={{ height: 30 }} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  workflow: selectWorkflow(state),
});

const mapDispatchToProps = (dispatch) => ({
  getMyDemands: () => dispatch(getMyDemands()),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyDemands);
